import Image from 'next/image';
import NextLink from 'next/link';

import { Accordion, Button, Card, Link, Text } from '@/atoms';
import { ButtonSize, ButtonVariant } from '@/atoms/button/types';
import { LinkVariant } from '@/atoms/link/types';

import type { FunctionComponent } from 'react';
import type { TypePrefooterProps } from './types';

/**
 * Prefooter
 */
export const Prefooter: FunctionComponent<TypePrefooterProps> = ({
  className = '',
  title,
  description,
  accordions,
  link,
  card,
  footerStyle,
}: TypePrefooterProps) =>
  footerStyle === 'boxed' ? (
    <div
      className={`flex flex-col items-center gap-6 lg:flex-row lg:items-start ${className}`}
    >
      <div className="rounded-custom w-full bg-neutrals-0 px-4 py-6 md:px-16 md:py-10">
        <Text
          className="text-headline text-headline--h3 mb-4 text-primary-900"
          as="h2"
        >
          {title}
        </Text>

        <Text className="text-body text-body--l mb-6 text-primary-900" as="p">
          {description}
        </Text>

        <div className="mb-6 flex flex-col gap-y-4">
          {accordions.map((content, index: number) => (
            <Accordion
              title={content.title}
              variant="normal"
              color="white"
              key={index}
            >
              {content.description}
            </Accordion>
          ))}
        </div>

        <Link
          href={link.href}
          className="text-primary-500"
          icon="right"
          variant={LinkVariant.NEGATIVE}
        >
          {link.label}
        </Link>
      </div>

      <Card
        className={{
          card: 'rounded-custom w-full bg-neutrals-0 md:max-w-sm',
          content: 'rounded-custom text-body bg-neutrals-0 px-8 py-12',
        }}
        shadow={true}
      >
        <Text className="fond-bold text-headline text-headline--h4 mb-2 text-primary-900">
          {card.title}
        </Text>

        {card?.image?.src && (
          <div className={`object-contain' relative h-[220px] w-full`}>
            <Image
              alt={card?.image?.alt ?? 'Imagen de la tarjeta'}
              src={card?.image?.src}
              draggable={false}
              fill
              sizes="(max-width: 768px) 100vw,
                (max-width: 1200px) 50vw,
                33vw"
              className="h-[270px] w-full object-contain p-4"
            />
          </div>
        )}

        <Text className="text-body--l mb-6 text-center text-primary-900">
          {card.description}
        </Text>

        <div className="mt-20 flex justify-center">
          <NextLink href={card.link.href} prefetch={false}>
            <Button
              as="span"
              size={ButtonSize.MEDIUM}
              variant={ButtonVariant.PRIMARY}
              className="!w-max !bg-secondary-500"
            >
              {card.link.label}
            </Button>
          </NextLink>
        </div>
      </Card>
    </div>
  ) : (
    <div
      className={`flex flex-col items-center gap-6 lg:flex-row lg:items-start ${className}`}
    >
      <div className="rounded-custom w-full bg-primary-500 px-4 py-6 md:px-16 md:py-10">
        <Text
          className="text-headline text-headline--h3 mb-4 text-neutrals-0"
          as="h2"
        >
          {title}
        </Text>

        <Text className="text-body text-body--l mb-6 text-neutrals-0" as="p">
          {description}
        </Text>

        <div className="mb-6 flex flex-col gap-y-4">
          {accordions.map((content, index: number) => (
            <Accordion title={content.title} variant="normal" key={index}>
              {content.description}
            </Accordion>
          ))}
        </div>

        <Link href={link.href} icon="right" variant={LinkVariant.NEGATIVE}>
          {link.label}
        </Link>
      </div>

      <Card
        className={{
          card: 'rounded-custom w-full bg-neutrals-50 md:max-w-sm',
          content: 'rounded-custom text-body bg-neutrals-50 px-8 py-12',
          image: 'h-[320px] w-full object-contain',
        }}
        image={card.image}
        shadow={false}
      >
        <Text className="fond-bold text-headline text-headline--h4 mb-2">
          {card.title}
        </Text>

        <Text className="text-body--l mb-6">{card.description}</Text>

        <NextLink href={card.link.href} prefetch={false}>
          <Button
            as="span"
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.SECONDARY}
            className="!w-max text-text-neutrals-900"
          >
            {card.link.label}
          </Button>
        </NextLink>
      </Card>
    </div>
  );

Prefooter.displayName = 'Prefooter';
